import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import TypographyLevel from 'shared/components/Typography';
import {
    AppFormikTextField,
    AppFormikTextFieldNumber,
    ButtonComp,
} from 'shared/components/AppComponents';
import React, {
    ReactElement,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { SolicitorData } from 'modules/authentication/components/ManageClientsPage';
import { ActionClientMeta, FormikPropsValues } from '../ActionClient';

/**
 * Component for displaying solicitor details.
 * @param {SolicitorDetailsProps} props - The component props.
 * @returns {ReactElement} React element representing the solicitor details.
 */
const SolicitorDetails: React.FC<SolicitorDetails> = ({
    validateAddCompany,
    validateAddSolicitor,
    onSolicitorDetailUpdated,
}): ReactElement => {
    const theme = useTheme();
    const ActionClientMetaProps = useContext(ActionClientMeta);
    const matches = useMediaQuery(theme.breakpoints.down('md'));
    const matchesMedia = useMediaQuery(theme.breakpoints.up('md'));
    const {
        values,
        touched,
        setFieldValue,
        setFieldTouched,
        getFieldHelpers,
        setValues,
        setTouched,
    } = useFormikContext<FormikPropsValues>();

    const [addCompany, setAddCompany] = useState<string>('');

    const [layoutChanges, setLayoutChanges] = useState<boolean>(false);
    const [layoutOne, setLayoutOne] = useState<Array<Array<number>>>([]);
    const [layoutTwo, setLayoutTwo] = useState<Array<Array<number>>>([]);
    const [layoutThree, setLayoutThree] = useState<Array<Array<number>>>([]);
    const textInputRef = useRef<Array<Array<HTMLInputElement>>>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [buttonRemoveSolicitor, setRemoveSolicitor] = useState<
        Array<number | undefined>
    >([]);
    const [isSolicitorAdded, setSolicitorAdded] = useState<boolean>(false);
    const [isSolicitorKeyPrimary, setSolicitorKeyPrimary] = useState<number>(0);
    const [isSolicitorKeyMain, setSolicitorKeyMain] = useState<number>(0);
    const newSolicitor: Solicitor = {
        name: '',
        email: '',
        phone: '',
        isAdded: false,
    };

    /**
     * Handles the addition of a solicitor.
     * @param {string} solicitorName - The name of the solicitor.
     * @returns {void}
     */
    const handleAdd = (solicitorName: string): void => {
        /**
         * Add company.
         *
         * @param {React.Dispatch<React.SetStateAction<any[]>>} setSolicitorData
         */

        const updatedData = [...values.solicitorData];
        const mergeData = updatedData.concat([
            {
                companyName: solicitorName,
                contacts: [
                    {
                        ...newSolicitor,
                        isAdded: true,
                    },
                ],
            },
        ]);

        setFieldValue('solicitorData', mergeData, false);
        setFieldTouched('solicitorCompany', false);
        getFieldHelpers('solicitorCompany').setValue('');
        setAddCompany('');
        setSolicitorKeyPrimary(mergeData.length - 1);
        setSolicitorKeyMain(-1);
        setSolicitorAdded(true);
        if (typeof onSolicitorDetailUpdated === 'function') {
            onSolicitorDetailUpdated();
        }
    };

    /**
     * Handles the removal of a solicitor.
     * @param {string} solicitorName - The name of the solicitor.
     * @param {number} index - The index of the solicitor.
     * @returns {any[]}
     */
    const handleRemove = (
        solicitorName: string,
        index: number,
        dataIndex: number
    ): void => {
        /**
         * Remove the data of solicitor.
         *
         * @param {React.Dispatch<React.SetStateAction<any[]>>} setSolicitorData
         */

        const updatedData = [...values.solicitorData];
        updatedData[index].contacts.splice(dataIndex, 1);
        setFieldValue('solicitorData', updatedData, true);
        if (typeof onSolicitorDetailUpdated === 'function') {
            onSolicitorDetailUpdated();
        }
    };

    /**
     * Handles the removal of a solicitor.
     * @param {string} solicitorName - The name of the solicitor.
     * @param {number} index - The index of the solicitor.
     * @returns {void}
     */
    const handleChange = (
        solicitorName: string,
        index: number,
        dataIndex: number,
        field: 'email' | 'phone' | 'name',
        value: string | number
    ): void => {
        /**
         * Updates the data of solicitors.
         *
         * @param {React.Dispatch<React.SetStateAction<any[]>>} setSolicitorData
         * @param {number} index - The index of the solicitor data to update.
         * @param {string} solicitorName - The name of the solicitor to update.
         * @param {string} field - The field of the solicitor data to update.
         * @param {string} value - The new value to assign to the specified field.
         * @returns {any[]}
         */

        const updatedData = [...values.solicitorData];
        if (updatedData[index] && updatedData[index].contacts[dataIndex]) {
            if (field === 'email' && typeof value === 'string') {
                updatedData[index].contacts[dataIndex].email = value;
            } else if (field === 'name' && typeof value === 'string') {
                updatedData[index].contacts[dataIndex].name = value;
            } else if (field === 'phone' && typeof value === 'number') {
                updatedData[index].contacts[dataIndex].phone = value;
            }
        }
        setFieldValue('solicitorData', updatedData, true);
    };

    /**
     * Handles the addition of a solicitor within a company.
     * @param {string} solicitorName - The name of the solicitor.
     * @param {number} index - The index of the solicitor.
     * @returns {void}
     */
    const handleAddSolicitor = React.useCallback(
        (solicitorName: string, index: number, dataIndex: number): void => {
            /**
             * Add solicitor.
             *
             * @param {React.Dispatch<React.SetStateAction<any[]>>} setSolicitorData
             * @param {number} index - The index of the solicitor data to update.
             * @param {string} solicitorName - The name of the solicitor to update.
             * @returns {any[]}
             */

            const updatedData = [...values.solicitorData];
            updatedData[index].contacts[dataIndex].isAdded = true;
            updatedData[index].contacts = updatedData[index].contacts.concat([
                newSolicitor,
            ]);
            setFieldValue('solicitorData', updatedData, true);
            if (typeof onSolicitorDetailUpdated === 'function') {
                onSolicitorDetailUpdated();
            }
        },
        [
            newSolicitor,
            onSolicitorDetailUpdated,
            setFieldValue,
            values.solicitorData,
        ]
    );

    const handleAddSolicitorToContacts = React.useCallback(
        async (index: number, dataIndex: number, solicitorName: string) => {
            const solicitorData = values?.solicitorData;
            if (solicitorData) {
                solicitorData[index].contacts[dataIndex].isAdded = true;
                setFieldValue(`solicitorData`, solicitorData, true);

                const validate = await validateAddSolicitor(index, dataIndex);
                setSolicitorKeyPrimary(index);
                setSolicitorKeyMain(dataIndex);
                if (validate) {
                    handleAddSolicitor(solicitorName, index, dataIndex);
                    setSolicitorAdded(true);
                } else {
                    solicitorData[index].contacts[dataIndex].isAdded = false;
                    solicitorData[index].contacts[dataIndex].isAddition = true;
                    textInputRef.current[index][dataIndex].focus();
                    setFieldValue(`solicitorData`, solicitorData, false);
                }
            }
        },
        [
            handleAddSolicitor,
            setFieldValue,
            validateAddSolicitor,
            values.solicitorData,
        ]
    );

    useLayoutEffect(() => {
        if (isSolicitorAdded) {
            textInputRef.current[isSolicitorKeyPrimary][
                isSolicitorKeyMain + 1
            ].focus();
            const { setValue } = getFieldHelpers(
                `solicitorData[${isSolicitorKeyPrimary}].contacts[${
                    isSolicitorKeyMain + 1
                }].isAddition`
            );

            const { setTouched } = getFieldHelpers(
                `solicitorData[${isSolicitorKeyPrimary}].contacts[${
                    isSolicitorKeyMain + 1
                }].name`
            );

            setTouched(true);
            setValue(true);
            setSolicitorAdded(false);
        }
    }, [isSolicitorAdded]);
    const handleIsValidateValue = React.useCallback(
        (
            fieldArray: FieldArrayRenderProps,
            parentField: string,
            indices: number
        ) => {
            const { form } = fieldArray;
            const { getFieldMeta, getFieldHelpers } = form;
            const { value } = getFieldMeta(`${parentField}.isAdded`);
            const { value: validate } = getFieldMeta(
                `${parentField}.isValidate`
            );
            const { setValue } = getFieldHelpers(`${parentField}.isValidate`);
            const { touched: name } = getFieldMeta(`${parentField}.name`);
            const { touched: email } = getFieldMeta(`${parentField}.email`);
            const { touched: phone } = getFieldMeta(`${parentField}.phone`);
            const touchy =
                (name || email || phone) &&
                indices >= 1 &&
                ActionClientMetaProps?.isSaveButtonFocus === true
                    ? false
                    : name || email || phone;

            const field =
                typeof value === 'boolean' && value === true ? false : touchy;
            if (validate !== field) {
                setValue(field);
            }

            return field;
        },
        [ActionClientMetaProps?.isSaveButtonFocus, touched]
    );

    useLayoutEffect(() => {
        if (ActionClientMetaProps?.isSaveButtonFocus) {
            setValues(
                {
                    ...values,
                    solicitorData: Array.from(values.solicitorData).map(
                        (t) => ({
                            ...t,
                            contacts: Array.from(t.contacts).map((c, i) => ({
                                ...c,
                                isFocus: i >= 1 ? false : c.isFocus,
                            })),
                        })
                    ),
                },
                true
            );
            setTouched(
                {
                    ...touched,
                    solicitorData: Array.from(touched.solicitorData ?? []).map(
                        (t) => {
                            if (typeof t !== 'undefined') {
                                return {
                                    ...t,
                                    contacts: Array.from(t.contacts ?? []).map(
                                        (c, i) => {
                                            if (typeof c !== 'undefined') {
                                                return {
                                                    ...c,
                                                    name:
                                                        i >= 1 ? false : c.name,
                                                    email:
                                                        i >= 1
                                                            ? false
                                                            : c.email,
                                                    phone:
                                                        i >= 1
                                                            ? false
                                                            : c.phone,
                                                };
                                            }
                                            return c;
                                        }
                                    ),
                                };
                            }
                            return t;
                        }
                    ),
                },
                true
            );
        }
    }, [ActionClientMetaProps?.isSaveButtonFocus]);

    useLayoutEffect(() => {
        if (layoutChanges) {
            const button = Array.from(values.solicitorData)
                .map((t, i) =>
                    Array.from(t.contacts).map((c, index) => {
                        const flatC = [
                            layoutOne[i][index],
                            layoutTwo[i][index],
                            layoutThree[i][index],
                        ];
                        const sortedFlatC = flatC.sort((a, b) => a - b);

                        return sortedFlatC.at(2);
                    })
                )
                .flat();
            setRemoveSolicitor(button);

            setLayoutChanges(false);
        }
    }, [layoutChanges]);

    return (
        <Box mt={8} className="solicitor-content">
            <TypographyLevel variant="two">Reviewer details</TypographyLevel>
            {/* Dynamic Form */}

            <FieldArray
                name="solicitorData"
                render={() => (
                    <>
                        {values.solicitorData.map(
                            (
                                solicitor: SolicitorData,
                                index: number,
                                arraySolicitor
                            ) => {
                                const solicitorName = solicitor.companyName;
                                const solicitorDetails = solicitor.contacts;
                                const booked =
                                    index !== 0
                                        ? arraySolicitor[index - 1].contacts
                                        : [];
                                const lengthed = booked ? booked.length : 0;

                                const start = index !== 0 ? lengthed : 0;
                                return (
                                    <Box
                                        className="solicitor-box"
                                        mt={8}
                                        key={solicitorName}
                                    >
                                        <TypographyLevel variant="three">
                                            {solicitorName}
                                        </TypographyLevel>

                                        <Box className="container-solicitor-input">
                                            <FieldArray
                                                name="contacts"
                                                render={(fieldArray) => (
                                                    <>
                                                        {solicitorDetails.map(
                                                            (
                                                                data,
                                                                dataIndex: number,
                                                                arrDetails: any[]
                                                            ) => (
                                                                <Grid
                                                                    container
                                                                    spacing={5}
                                                                    key={
                                                                        dataIndex
                                                                    }
                                                                    className="solicitor-row-input"
                                                                >
                                                                    <AppFormikTextField
                                                                        name={`solicitorData[${index}].contacts[${dataIndex}].isAdded`}
                                                                        inputProps={{
                                                                            type: 'hidden',
                                                                        }}
                                                                        className="isAdded"
                                                                    />
                                                                    <AppFormikTextField
                                                                        name={`solicitorData[${index}].contacts[${dataIndex}].isAddition`}
                                                                        inputProps={{
                                                                            type: 'hidden',
                                                                            value: data.isAddition,
                                                                        }}
                                                                        className="isAdded"
                                                                    />
                                                                    <AppFormikTextField
                                                                        name={`solicitorData[${index}].contacts[${dataIndex}].isFocus`}
                                                                        inputProps={{
                                                                            type: 'hidden',
                                                                        }}
                                                                        className="isAdded"
                                                                    />
                                                                    <AppFormikTextField
                                                                        name={`solicitorData[${index}].contacts[${dataIndex}].isValidate`}
                                                                        inputProps={{
                                                                            type: 'hidden',
                                                                            value: handleIsValidateValue(
                                                                                fieldArray,
                                                                                `solicitorData[${index}].contacts[${dataIndex}]`,
                                                                                dataIndex
                                                                            ),
                                                                        }}
                                                                        className="isAdded"
                                                                    />
                                                                    <Grid
                                                                        item
                                                                        xs={6}
                                                                        sm={6}
                                                                        md={2}
                                                                        lg={3}
                                                                        xl={3}
                                                                        className="solicitor-input"
                                                                    >
                                                                        <TypographyLevel
                                                                            variant="four"
                                                                            className="solicitor-name"
                                                                        >
                                                                            Reviewer
                                                                            name
                                                                        </TypographyLevel>
                                                                        <AppFormikTextField
                                                                            name={`solicitorData[${index}].contacts[${dataIndex}].name`}
                                                                            placeholder="Type here"
                                                                            className=" solicitorInput"
                                                                            inputRef={(
                                                                                ref: any
                                                                            ) => {
                                                                                if (
                                                                                    typeof textInputRef
                                                                                        .current[
                                                                                        index
                                                                                    ] ===
                                                                                    'undefined'
                                                                                ) {
                                                                                    textInputRef.current[
                                                                                        index
                                                                                    ] =
                                                                                        [];
                                                                                    textInputRef.current[
                                                                                        index
                                                                                    ][
                                                                                        dataIndex
                                                                                    ] =
                                                                                        ref;
                                                                                } else {
                                                                                    const inputArray: any[] =
                                                                                        textInputRef
                                                                                            .current[
                                                                                            index
                                                                                        ];
                                                                                    inputArray[
                                                                                        dataIndex
                                                                                    ] =
                                                                                        ref;
                                                                                    textInputRef.current[
                                                                                        index
                                                                                    ] =
                                                                                        inputArray;
                                                                                }
                                                                            }}
                                                                            onChange={(
                                                                                ev: any
                                                                            ) =>
                                                                                handleChange(
                                                                                    solicitorName,
                                                                                    index,
                                                                                    dataIndex,
                                                                                    'name',
                                                                                    ev
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            onBlur={() => {
                                                                                const {
                                                                                    setValue,
                                                                                } =
                                                                                    getFieldHelpers(
                                                                                        `solicitorData[${index}].contacts[${dataIndex}].isAddition`
                                                                                    );

                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].name`
                                                                                        );
                                                                                    const {
                                                                                        setValue:
                                                                                            setValueFocused,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );
                                                                                    setValueFocused(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                    setTouched(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                } else if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition ===
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched:
                                                                                            setTouchedName,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].name`
                                                                                        );
                                                                                    const {
                                                                                        setTouched:
                                                                                            setTouchedEmail,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].email`
                                                                                        );
                                                                                    const {
                                                                                        setTouched:
                                                                                            setTouchedPhone,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].phone`
                                                                                        );
                                                                                    setTouchedName(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                    setTouchedPhone(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                    setTouchedEmail(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                }
                                                                                setValue(
                                                                                    false,
                                                                                    true
                                                                                );
                                                                            }}
                                                                            onFocus={() => {
                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].name`
                                                                                        );
                                                                                    const {
                                                                                        setValue,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );
                                                                                    setValue(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                    setTouched(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onElementSizing={(
                                                                                layout: any
                                                                            ) => {
                                                                                setLayoutOne(
                                                                                    (
                                                                                        prev: any
                                                                                    ) => {
                                                                                        const layouter =
                                                                                            prev;
                                                                                        // eslint-disable-next-line @typescript-eslint/no-shadow
                                                                                        let layoutOne =
                                                                                            layouter[
                                                                                                index
                                                                                            ];

                                                                                        if (
                                                                                            typeof layoutOne ===
                                                                                            'undefined'
                                                                                        ) {
                                                                                            layoutOne =
                                                                                                [];
                                                                                            layoutOne[
                                                                                                dataIndex
                                                                                            ] =
                                                                                                layout.height;

                                                                                            layouter[
                                                                                                index
                                                                                            ] =
                                                                                                layoutOne;

                                                                                            return layouter;
                                                                                        }

                                                                                        layoutOne[
                                                                                            dataIndex
                                                                                        ] =
                                                                                            layout.height;
                                                                                        layouter[
                                                                                            index
                                                                                        ] =
                                                                                            layoutOne;
                                                                                        return layouter;
                                                                                    }
                                                                                );
                                                                                setLayoutChanges(
                                                                                    true
                                                                                );
                                                                            }}
                                                                        />
                                                                    </Grid>

                                                                    <Grid
                                                                        item
                                                                        xs={6}
                                                                        sm={6}
                                                                        md={4}
                                                                        lg={4}
                                                                        xl={5}
                                                                        className="solicitor-input"
                                                                    >
                                                                        <TypographyLevel
                                                                            variant="four"
                                                                            className="solicitor-email"
                                                                        >
                                                                            Reviewer
                                                                            email
                                                                        </TypographyLevel>
                                                                        <AppFormikTextField
                                                                            name={`solicitorData[${index}].contacts[${dataIndex}].email`}
                                                                            className="solicitorInput"
                                                                            placeholder="Type here"
                                                                            onChange={(
                                                                                ev: any
                                                                            ) =>
                                                                                handleChange(
                                                                                    solicitorName,
                                                                                    index,
                                                                                    dataIndex,
                                                                                    'email',
                                                                                    ev
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            onElementSizing={(
                                                                                layout: any
                                                                            ) => {
                                                                                setLayoutTwo(
                                                                                    (
                                                                                        prev: any
                                                                                    ) => {
                                                                                        const layouter =
                                                                                            prev;
                                                                                        // eslint-disable-next-line @typescript-eslint/no-shadow
                                                                                        let layoutTwo =
                                                                                            layouter[
                                                                                                index
                                                                                            ];
                                                                                        if (
                                                                                            typeof layoutTwo ===
                                                                                            'undefined'
                                                                                        ) {
                                                                                            layoutTwo =
                                                                                                [];
                                                                                            layoutTwo[
                                                                                                dataIndex
                                                                                            ] =
                                                                                                layout.height;
                                                                                            layouter[
                                                                                                index
                                                                                            ] =
                                                                                                layoutTwo;
                                                                                            return layouter;
                                                                                        }
                                                                                        layoutTwo[
                                                                                            dataIndex
                                                                                        ] =
                                                                                            layout.height;
                                                                                        layouter[
                                                                                            index
                                                                                        ] =
                                                                                            layoutTwo;
                                                                                        return layouter;
                                                                                    }
                                                                                );
                                                                                setLayoutChanges(
                                                                                    true
                                                                                );
                                                                            }}
                                                                            onBlur={() => {
                                                                                const {
                                                                                    setValue,
                                                                                } =
                                                                                    getFieldHelpers(
                                                                                        `solicitorData[${index}].contacts[${dataIndex}].isAddition`
                                                                                    );

                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].email`
                                                                                        );
                                                                                    const {
                                                                                        setValue:
                                                                                            setValueFocus,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );
                                                                                    setValueFocus(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                    setTouched(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                }
                                                                                setValue(
                                                                                    false,
                                                                                    true
                                                                                );
                                                                            }}
                                                                            onFocus={() => {
                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].email`
                                                                                        );
                                                                                    const {
                                                                                        setValue,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );
                                                                                    setValue(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                    setTouched(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>

                                                                    <Grid
                                                                        xs={6}
                                                                        sm={6}
                                                                        md={2}
                                                                        item
                                                                        className="solicitor-input"
                                                                    >
                                                                        <TypographyLevel
                                                                            variant="four"
                                                                            className="solicitor-phone"
                                                                        >
                                                                            Reviewer
                                                                            phone
                                                                        </TypographyLevel>
                                                                        <AppFormikTextFieldNumber
                                                                            name={`solicitorData[${index}].contacts[${dataIndex}].phone`}
                                                                            className=" solicitorInput"
                                                                            placeholder="Type here"
                                                                            onChange={(
                                                                                ev: any
                                                                            ) =>
                                                                                handleChange(
                                                                                    solicitorName,
                                                                                    index,
                                                                                    dataIndex,
                                                                                    'phone',
                                                                                    parseInt(
                                                                                        ev.target.value.replace(
                                                                                            /\D/,
                                                                                            ''
                                                                                        ),
                                                                                        10
                                                                                    )
                                                                                )
                                                                            }
                                                                            onElementSizing={(
                                                                                layout: any
                                                                            ) => {
                                                                                setLayoutThree(
                                                                                    (
                                                                                        prev: any
                                                                                    ) => {
                                                                                        const layouter =
                                                                                            prev;
                                                                                        // eslint-disable-next-line @typescript-eslint/no-shadow
                                                                                        let layoutThree =
                                                                                            layouter[
                                                                                                index
                                                                                            ];
                                                                                        if (
                                                                                            typeof layoutThree ===
                                                                                            'undefined'
                                                                                        ) {
                                                                                            layoutThree =
                                                                                                [];
                                                                                            layoutThree[
                                                                                                dataIndex
                                                                                            ] =
                                                                                                layout.height;
                                                                                            layouter[
                                                                                                index
                                                                                            ] =
                                                                                                layoutThree;
                                                                                            return layouter;
                                                                                        }
                                                                                        layoutThree[
                                                                                            dataIndex
                                                                                        ] =
                                                                                            layout.height;
                                                                                        layouter[
                                                                                            index
                                                                                        ] =
                                                                                            layoutThree;
                                                                                        return layouter;
                                                                                    }
                                                                                );
                                                                                setLayoutChanges(
                                                                                    true
                                                                                );
                                                                            }}
                                                                            onBlur={() => {
                                                                                const {
                                                                                    setValue,
                                                                                } =
                                                                                    getFieldHelpers(
                                                                                        `solicitorData[${index}].contacts[${dataIndex}].isAddition`
                                                                                    );

                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].phone`
                                                                                        );
                                                                                    const {
                                                                                        setValue:
                                                                                            setValueFocused,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );
                                                                                    setValueFocused(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                    setTouched(
                                                                                        false,
                                                                                        false
                                                                                    );
                                                                                }
                                                                                setValue(
                                                                                    false,
                                                                                    true
                                                                                );
                                                                            }}
                                                                            onFocus={() => {
                                                                                if (
                                                                                    arrDetails.length >=
                                                                                        2 &&
                                                                                    data.isAddition !==
                                                                                        true
                                                                                ) {
                                                                                    const {
                                                                                        setTouched,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].phone`
                                                                                        );
                                                                                    const {
                                                                                        setValue,
                                                                                    } =
                                                                                        getFieldHelpers(
                                                                                            `solicitorData[${index}].contacts[${dataIndex}].isFocus`
                                                                                        );

                                                                                    setValue(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                    setTouched(
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>

                                                                    <Grid
                                                                        item
                                                                        xs={
                                                                            dataIndex !==
                                                                            solicitorDetails.length -
                                                                                1
                                                                                ? 12
                                                                                : 12
                                                                        }
                                                                        sm={
                                                                            dataIndex !==
                                                                            solicitorDetails.length -
                                                                                1
                                                                                ? 6
                                                                                : 12
                                                                        }
                                                                        md={
                                                                            dataIndex !==
                                                                            solicitorDetails.length -
                                                                                1
                                                                                ? 4
                                                                                : 12
                                                                        }
                                                                        lg={
                                                                            dataIndex !==
                                                                            solicitorDetails.length -
                                                                                1
                                                                                ? 3
                                                                                : 12
                                                                        }
                                                                        xl={
                                                                            dataIndex !==
                                                                            solicitorDetails.length -
                                                                                1
                                                                                ? 2
                                                                                : 12
                                                                        }
                                                                        className="solicitor-input"
                                                                    >
                                                                        {dataIndex ===
                                                                        solicitorDetails.length -
                                                                            1 ? (
                                                                            <ButtonComp
                                                                                type="neutral"
                                                                                width={
                                                                                    matchesMedia
                                                                                        ? '30%'
                                                                                        : '100%'
                                                                                }
                                                                                onClick={() => {
                                                                                    handleAddSolicitorToContacts(
                                                                                        index,
                                                                                        dataIndex,
                                                                                        solicitorName
                                                                                    );
                                                                                }}
                                                                            >
                                                                                Add
                                                                                Reviewer
                                                                            </ButtonComp>
                                                                        ) : (
                                                                            <ButtonComp
                                                                                type="neutral"
                                                                                width="100%"
                                                                                className="remove-solicitor-button"
                                                                                disabled={
                                                                                    arrDetails.length ===
                                                                                    2
                                                                                }
                                                                                onClick={() => {
                                                                                    handleRemove(
                                                                                        solicitorName,
                                                                                        index,
                                                                                        dataIndex
                                                                                    );
                                                                                }}
                                                                                sx={{
                                                                                    marginBottom: `${
                                                                                        buttonRemoveSolicitor[
                                                                                            start +
                                                                                                dataIndex
                                                                                        ]
                                                                                            ? (buttonRemoveSolicitor[
                                                                                                  start +
                                                                                                      dataIndex
                                                                                              ] ??
                                                                                                  0) -
                                                                                              55 +
                                                                                              9
                                                                                            : 9
                                                                                    }px !important`,
                                                                                }}
                                                                            >
                                                                                Remove
                                                                                Reviewer
                                                                            </ButtonComp>
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </Box>
                                    </Box>
                                );
                            }
                        )}
                    </>
                )}
            />

            <Grid
                container
                spacing={matches ? 0 : 5}
                className="solicitor-input-company"
            >
                <Grid item xs={12} md={9}>
                    <TypographyLevel variant="four">
                        Reviewer company name
                    </TypographyLevel>
                    <AppFormikTextField
                        name="solicitorCompany"
                        placeholder="Type here"
                        value={addCompany}
                        onChange={(ev: any) => {
                            setAddCompany(ev.target.value);
                        }}
                        // className="styled-action-client"
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <TypographyLevel variant="four">&nbsp;</TypographyLevel>
                    <Box>
                        <ButtonComp
                            type="neutral"
                            width="100%"
                            onClick={() => {
                                const validate = validateAddCompany(
                                    values.solicitorData
                                );
                                if (validate) {
                                    validate.then((bool) => {
                                        if (bool) {
                                            handleAdd(addCompany);
                                        }
                                    });
                                }
                            }}
                        >
                            Add company
                        </ButtonComp>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SolicitorDetails;
